import { useEffect, useRef } from 'react';
import './orgChart.css';

/**
 * Export-to-PDF dialog. Two documents: the view the reader is looking at, or the
 * job-positions sheet.
 *
 * Deliberately plain about the mechanism. The export is the browser's own print
 * dialog with "Save as PDF" chosen as the destination, and saying so up front is
 * better than a button that appears to generate a file and instead opens a
 * system dialog the reader then has to understand.
 */
export default function OrgPrintDialog({ open, onPrint, onClose }) {
  const panelRef = useRef(null);

  // Escape closes, matching every other dismissible surface in the app.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Move focus into the panel on open so a keyboard reader is not left with
  // focus on a now-hidden trigger behind the backdrop.
  useEffect(() => {
    if (open && panelRef.current) panelRef.current.focus();
  }, [open]);

  if (!open) return null;

  return (
    <div className="oc-print-backdrop" onClick={onClose}>
      {/* Stops a click inside the panel from reaching the backdrop's close
          handler. */}
      <div
        className="oc-print-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="oc-print-title"
        tabIndex={-1}
        ref={panelRef}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="oc-print-title" id="oc-print-title">Export as PDF</h2>
        <p className="oc-print-help">
          Opens your browser&apos;s print dialog. Choose <strong>Save as PDF</strong> as
          the destination. The page is already set to landscape. Keep
          <strong> Background graphics</strong> ticked, or the status pills print
          without their colour.
        </p>
        <p className="oc-print-help">
          The PDF lists each manager followed by their direct reports, so the whole
          chart fits across as many pages as it needs.
        </p>

        <div className="oc-print-actions">
          <button
            type="button"
            className="btn"
            onClick={() => onPrint('all')}
          >
            Export current view
          </button>
        </div>

        {/* A structurally different document, so it gets its own block rather
            than sitting beside "Export current view" as if it were a variant of
            it: one card per JOB with the people removed, drawn as a tree and
            scaled onto a single sheet. */}
        <p className="oc-print-label">Or export the job positions only</p>
        <p className="oc-print-help">
          One card per position, no employee names, as a tree on a single
          landscape page.
        </p>
        <div className="oc-print-actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => onPrint('all', { mode: 'positions' })}
          >
            Export job positions
          </button>
        </div>

        <div className="oc-print-close">
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
